import { boardblendApi } from "@/store/api";
import type {
  ArtifactExportFormat,
  ArtifactExportSnapshot,
} from "@/components/boardblend/artifact-export-client";

interface SubmitFeedbackArgs {
  sessionId?: string;
  text: string;
}

interface SessionDownload {
  blob: Blob;
  fileName: string;
}

export interface CanvasExportArgs {
  format: ArtifactExportFormat;
  sessionId: string;
  snapshot: ArtifactExportSnapshot;
  title: string;
  versionId: string;
}

export interface CanvasExportDownload {
  blob: Blob;
  fileName: string;
}

const utilityApi = boardblendApi.injectEndpoints({
  endpoints: (builder) => ({
    exportCanvas: builder.mutation<CanvasExportDownload, CanvasExportArgs>({
      query: ({ format, sessionId, snapshot, versionId }) => ({
        body: { format, snapshot, versionId },
        method: "POST",
        responseHandler: readCanvasExportResponse,
        url: `sessions/${sessionId}/canvas-export`,
      }),
      transformResponse: (blob: Blob, meta, { format, title }) => ({
        blob,
        fileName: getCanvasExportFileName(
          meta?.response?.headers.get("Content-Disposition"),
          title,
          format,
        ),
      }),
    }),
    submitFeedback: builder.mutation<{ submitted: true }, SubmitFeedbackArgs>({
      query: (body) => ({
        body,
        method: "POST",
        url: "feedback",
      }),
    }),
    downloadSession: builder.mutation<SessionDownload, string>({
      query: (sessionId) => ({
        responseHandler: (response) => response.blob(),
        url: `sessions/${sessionId}/export`,
      }),
      transformResponse: (blob: Blob, meta) => ({
        blob,
        fileName: getDownloadFileName(
          meta?.response?.headers.get("Content-Disposition"),
        ),
      }),
    }),
  }),
});

export const {
  useDownloadSessionMutation,
  useExportCanvasMutation,
  useSubmitFeedbackMutation,
} = utilityApi;

export async function readCanvasExportResponse(response: Response) {
  if (response.ok) return response.blob();

  const body = (await response.json().catch(() => null)) as {
    error?: unknown;
  } | null;
  return {
    error:
      typeof body?.error === "string" ? body.error : "Canvas export failed",
  };
}

export function getCanvasExportFileName(
  contentDisposition: string | null | undefined,
  title: string,
  format: ArtifactExportFormat,
) {
  const encoded = contentDisposition?.match(
    /filename\*=UTF-8''([^;]+)/i,
  )?.[1];
  if (encoded) return decodeURIComponent(encoded);

  const plain = contentDisposition?.match(/filename="([^"]+)"/i)?.[1];
  return plain ?? `${title}.${format}`;
}

export function getDownloadFileName(contentDisposition?: string | null) {
  if (!contentDisposition?.toLowerCase().startsWith("attachment;")) {
    throw new Error("Download filename is missing");
  }

  const match = /(?:^|;)\s*filename\*=UTF-8''([^;]+)(?:;|$)/i.exec(
    contentDisposition,
  );
  if (!match) throw new Error("Download filename is missing");

  let fileName: string;
  try {
    fileName = decodeURIComponent(match[1]);
  } catch {
    throw new Error("Download filename is invalid");
  }

  if (
    !fileName ||
    fileName !== fileName.trim() ||
    /[\\/\u0000-\u001f\u007f]/.test(fileName)
  ) {
    throw new Error("Download filename is invalid");
  }

  return fileName;
}
